/* eslint-disable no-console */
import { h, createContext } from 'preact';
import { useContext, useEffect, useState, useMemo } from 'preact/hooks';
import tinycolor from 'tinycolor2';
import { productByHandle, productsByTag } from '../utils/storefront/queries';
import Storefront from '../utils/storefront';
import {
  getShortUrl,
  postBenchmadeData,
  getShortId,
  getThumbnailUrl,
  getThreekitConfiguration,
} from './components/util/fetchRequests';
import { uuidv4, getImageAndTextConfigurations, debug } from './utils';
import useRestrictedWords from '../utils/hooks/useRestrictedWords';
import useUpdateErrorsBasedOnValidation from '../utils/hooks/useUpdateErrorsBasedOnValidation';

const storefront = new Storefront();
const Context = createContext();

export const useThreekit = () => useContext(Context);

/**
 * @property {string} hex - hex color value for presets
 * @property {boolean} default - sets as default color. could also be assumed as index 0
 * @property {boolean} presetLightMode - enables addl dark background colors behind some components
 * @property {boolean} useProductColor - replaces `hex` with current product color
 */
const defaultAvailableBackgroundColors = [
  {
    default: true,
    useProductColor: true,
    hex: '#919191',
  },
  {
    hex: '#EAEAEB',
    presetLightMode: true,
  },
];

export const ThreekitContext = ({ children, data }) => {
  const [cartId, setCartId] = useState(uuidv4());
  const [attributes, setAttributes] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [bladeSide, setBladeSide] = useState('Front');
  const [products, setProducts] = useState(null);
  const [frontTextLine1, setFrontTextLine1] = useState('');
  const [frontTextLine2, setFrontTextLine2] = useState('');
  const [backTextLine1, setBackTextLine1] = useState('');
  const [backTextLine2, setBackTextLine2] = useState('');
  const [personalizeTextOne, setPersonalizeTextOne] = useState('');
  const [personalizeTextTwo, setPersonalizeTextTwo] = useState('');
  const [personalizeDuplicateSide, setPersonalizeDuplicateSide] =
    useState(false);
  const [bladeColorDuplicateSide, setBladeColorDuplicateSide] = useState(true);
  const [category, setCategory] = useState('All');
  const [frontCustomImage, setFrontCustomImage] = useState(false);
  const [backCustomImage, setBackCustomImage] = useState(false);
  const [isOnBlur, setIsOnBlur] = useState(false);
  const [frontImage, setFrontImage] = useState('');
  const [backImage, setBackImage] = useState('');
  const [knifeClickActive, setKnifeClickActive] = useState(false);
  const [hasFlaggedContent, setHasFlaggedContent] = useState(false);
  const [customImageFileUrl, setCustomImageFileUrl] = useState(null);
  const [errors, setErrors] = useState({});
  // backgroundColor defaults in background-selector.js
  const [backgroundColor, setBackgroundColor] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [cacheKey, setCacheKey] = useState(null);
  const productTag = window.customTag || data.customTag;
  const productHandle = window.handle || data.handle;
  const { env } = data;
  const imageSeverityData = JSON.parse(
    document.querySelector('[data-image-severity]').textContent,
  );

  const resetCartId = () => setCartId(uuidv4());

  useUpdateErrorsBasedOnValidation(
    'Add Front Text',
    useRestrictedWords(
      configuration?.['Add Front Text'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );
  useUpdateErrorsBasedOnValidation(
    'Add Front Text Line_2',
    useRestrictedWords(
      configuration?.['Add Front Text Line_2'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );
  useUpdateErrorsBasedOnValidation(
    'Add Back Text',
    useRestrictedWords(
      configuration?.['Add Back Text'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );
  useUpdateErrorsBasedOnValidation(
    'Add Back Text Line_2',
    useRestrictedWords(
      configuration?.['Add Back Text Line_2'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );

  useUpdateErrorsBasedOnValidation(
    'Add Text Line 1',
    useRestrictedWords(
      configuration?.['Add Text Line 1'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );

  useUpdateErrorsBasedOnValidation(
    'Add Text Line 2',
    useRestrictedWords(
      configuration?.['Add Text Line 2'],
      isOnBlur,
      setIsOnBlur,
      data.moderation,
    ),
    setErrors,
  );

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setHasFlaggedContent(true);
    } else {
      setHasFlaggedContent(false);
    }
  }, [errors]);

  useEffect(() => {
    if (configuration) {
      if (
        configuration['Front Handle Color']?.assetId !==
        configuration['Back Handle Color']?.assetId
      ) {
        setBladeColorDuplicateSide(false);
      }
    }
  }, [configuration]);

  useEffect(() => {
    const extractAttribute = (tag) => {
      // eslint-disable-next-line no-unused-vars
      const [_, attributeName, attributeValue] = tag
        .split(':')
        .map((str) => str.replace(/_/g, ' '));
      return { name: attributeName, value: attributeValue };
    };

    const getConfiguration = (attribute, config) => {
      const value = attribute.values.find((val) => val.name === config.value);
      if (value) {
        return { [attribute.name]: value };
      }
      return null;
    };

    const getShopifyProducts = async (formattedAttributes) => {
      try {
        const products = [];

        if (
          productTag === 'cstm:personalization' ||
          productTag === 'cstm:service'
        ) {
          const productResponse = await storefront.request({
            query: productByHandle(),
            variables: { handle: productHandle },
          });

          if (productResponse.data.productByHandle) {
            products.push(productResponse.data.productByHandle);
          }

          const personalizationResponse = await storefront.request({
            query: productByHandle(),
            variables: { handle: 'personalization-additional-items' },
          });

          if (personalizationResponse.data.productByHandle) {
            products.push(personalizationResponse.data.productByHandle);
          }
        } else {
          const response = await storefront.request({
            query: productsByTag(productTag, 40),
            variables: {},
          });
          products.push(...response.data.products.nodes);
        }

        products.forEach((product) => {
          const allConfigurations = []; // Array for all attributes associated with all variants

          product.variants.edges.forEach((variant) => {
            debug({
              variant,
              groupStart: true,
            });

            // All attributes to the variant without checking if the attribute exists
            const uncheckedConfigurations = [];

            // Will set the configuration to the specific variant.
            // This array checks if the attribute exists
            const configurations = [];

            product.customizerTitle =
              product.tags
                .find((tag) => tag.includes('CustomizerTitle'))
                ?.split(':')[1]
                ?.replace(/_/g, ' ')
                ?.trim() ?? product.title;

            // get Threekit tag
            const threeKitTags = product.tags.filter((tag) =>
              tag.includes('Threekit'),
            );

            // get Threekit tags with 2 ":"'s
            const withAttribute = threeKitTags
              .filter((tag) => /^([^:]*:){2}[^:]*$/.test(tag))
              .map(extractAttribute);

            // get Threekit tags with 1 ":"
            const withoutAttribute = threeKitTags
              .filter((tag) => /^([^:]*:){1}[^:]*$/.test(tag))
              .map((tag) => ({
                name: extractAttribute(tag).name,
                value: variant.node.title,
              }));

            // push all attributes to unchecked array
            withAttribute.forEach((attr) => {
              uncheckedConfigurations.push(attr);
            });

            if (variant.node.selectedOptions.length > 1) {
              variant.node.selectedOptions.forEach((option) => {
                uncheckedConfigurations.push({
                  name: option.name,
                  value: option.value,
                });
              });
            } else {
              withoutAttribute.forEach((attr) => {
                uncheckedConfigurations.push(attr);
              });
            }

            // Check if the attribute exists in Threekit
            uncheckedConfigurations.forEach((config) => {
              const attribute = formattedAttributes.find(
                (attr) => attr.name === config.name,
              );

              if (
                !attribute?.values?.find((val) => val.name === config.value)
              ) {
                debug({
                  variant,
                  message: `Attribute "${config.value}" not found in`,
                  data: attribute?.values,
                });
              }
              if (attribute) {
                const configuration = getConfiguration(attribute, config);
                if (configuration) {
                  configurations.push(configuration);
                  allConfigurations.push(config);
                } else if (
                  attribute.name.includes('Image') ||
                  attribute.name.includes('Art')
                ) {
                  configurations.push({
                    [attribute.name]: attribute.defaultValue,
                  });
                  allConfigurations.push({
                    name: attribute.name,
                    value: attribute.defaultValue,
                  });
                }
              }
            });

            variant.node.configurationEnabled =
              configurations.length > 0 &&
              variant.node.available &&
              variant.node.disableInCustomizer?.value !== 'true';

            debug({
              variant,
              message: 'Configurations enabled',
              data: !!variant.node.configurationEnabled,
            });

            if (!variant.node.configurationEnabled) {
              if (configurations.length < 1) {
                debug({
                  variant,
                  message: 'No configurations found',
                });
              }
              if (!variant.node.available) {
                debug({
                  variant,
                  message: 'Variant not available',
                });
              }
              if (variant.node.disableInCustomizer?.value === 'true') {
                debug({
                  variant,
                  message: 'Variant disabled in customizer',
                });
              }
            }
            debug({
              groupEnd: true,
            });

            variant.node.configuration = configurations.reduce(
              (acc, obj) => ({ ...acc, ...obj }),
              {},
            );

            // If the unchecked array length does not match the length of the configuartion array,
            // this means that not all configurations were found. We set configurationEnabled
            // to false and we set the configruation object to and empty object.
            if (
              uncheckedConfigurations.length !== configurations.length ||
              uncheckedConfigurations.length === 0
            ) {
              variant.node.configurationEnabled = false;
              variant.node.configuration = {};
            }
          });

          if (allConfigurations.length > 0) {
            product.allConfigurations = Array.from(
              new Set(allConfigurations.map(JSON.stringify)),
              JSON.parse,
            );
          }
        });

        setProducts(products);
      } catch (error) {
        console.error(error);
      }
    };

    // A check to see if window.configurator exists
    const checkConfig = async () => {
      const configPromise = () =>
        new Promise((resolve) => {
          if (window.player) {
            resolve('Success');
          } else setTimeout(checkConfig, 250);
        });
      window.configurator = await window.player.getConfigurator();
      // This was a 3kit request, setFullConfiguration is called twice on purpose
      if (window.tkcsid) {
        const config = await getThreekitConfiguration(
          env,
          data.authToken,
          window.tkcsid,
        );
        await window.configurator.setFullConfiguration(config);
        await window.configurator.setFullConfiguration(config);
      }
      const configuratorInitialized = await configPromise();
      if (configuratorInitialized) {
        const rawAttributes = window.configurator.getAttributes();
        const configuration = window.configurator.getConfiguration();
        const formattedAttributes = rawAttributes.map((attribute) => ({
          name: attribute.name,
          defaultValue: attribute.defaultValue,
          values:
            // eslint-disable-next-line no-nested-ternary
            attribute.type === 'Boolean'
              ? [true, false]
              : attribute.type === 'Number'
              ? {
                  max: attribute.max,
                  min: attribute.min,
                  default: attribute.defaultValue,
                }
              : attribute.values,
        }));
        setAttributes(formattedAttributes);
        getShopifyProducts(formattedAttributes);

        // Set states of any images/texts
        const {
          frontImage,
          frontPattern,
          frontCustomImage,
          backCustomImage,
          backImage,
          backPattern,
          frontTextLine1,
          frontTextLine2,
          backTextLine1,
          backTextLine2,
          customArt,
          personalizeTextOne,
          personalizeTextTwo,
          personalizeFrontImage,
          personalizeUploadImage,
        } = getImageAndTextConfigurations(configuration);
        setFrontImage(
          frontImage || frontPattern || customArt || personalizeFrontImage,
        );
        setFrontCustomImage(frontCustomImage || !!personalizeUploadImage);
        setBackCustomImage(backCustomImage);
        setBackImage(backImage || backPattern);
        setFrontTextLine1(frontTextLine1);
        setFrontTextLine2(frontTextLine2);
        setBackTextLine1(backTextLine1);
        setBackTextLine2(backTextLine2);
        setPersonalizeTextOne(personalizeTextOne);
        setPersonalizeTextTwo(personalizeTextTwo);

        setConfiguration(configuration);
      }
    };

    checkConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { player } = window;
  const { configurator } = window;

  // This is used to get the configuration of the product with valid Shopify products/variants
  useEffect(() => {
    const checkConfig = async () => {
      let configurationsToSet = {};
      // Get all Variants
      const allVariantConfigs = products.flatMap((product) =>
        product.variants.edges
          .filter(
            (variant) =>
              variant.node.configurationEnabled && variant.node.configuration,
          )
          .map((variant) => variant.node.configuration),
      );

      // Group variants by last key in the array
      const groupedVariants = Object.values(
        allVariantConfigs.reduce((acc, curr) => {
          let name = Object.keys(curr)[Object.keys(curr).length - 1];
          // this if statement is needed because Handle Material
          // and Front Handle Color are dependant on each other.
          if (name === 'Handle Material') {
            name = 'Front Handle Color';
          }
          acc[name] = acc[name] || [];
          acc[name].push(curr);
          return acc;
        }, {}),
      );

      groupedVariants.forEach((group) => {
        const groupItemWithMostKeys = group.reduce((acc, curr) => {
          if (Object.keys(curr).length > Object.keys(acc).length) {
            return curr;
          }
          return acc;
        }, {});
        const getKeys = Object.keys(groupItemWithMostKeys);
        const currentConfigIds = getKeys.map(
          (key) => configuration[key]?.assetId,
        );
        const findConfiguration = group.find((g) => {
          const variantConfigIds = Object.values(g).map((v) => v?.assetId);
          return variantConfigIds.every((v) => currentConfigIds.includes(v));
        });

        if (!findConfiguration) {
          const handleMaterialId = configuration?.['Handle Material'].assetId;
          const groupContainedHandleMaterial = group.some(
            (g) => g?.['Handle Material'],
          );

          if (groupContainedHandleMaterial) {
            configurationsToSet = {
              ...configurationsToSet,
              ...group.find(
                (g) => handleMaterialId === g?.['Handle Material'].assetId,
              ),
            };
          } else {
            configurationsToSet = {
              ...configurationsToSet,
              ...group[0],
            };
          }
        }
      });
      if (!configurationsToSet['Choose Custom Art']) {
        await configurator.setConfiguration(configurationsToSet);
        setConfiguration(configurator.getConfiguration());
      }
    };

    if (products) {
      checkConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useMemo(() => configuration, [configuration]);

  const getProductsFromAttribute = (attributeName) => {
    const configurableProducts = products.filter((product) => {
      const configurations = product.allConfigurations;
      if (!configurations) {
        return false; // filter out products without configurations
      }
      return configurations.some((obj) => obj.name === attributeName);
    });

    return configurableProducts;
  };

  const getSelectedVariant = (products) => {
    const variant = products
      .flatMap((product) => product.variants.edges)
      .find(
        (variant) =>
          Object.entries(variant.node.configuration).every(
            ([variantKey, variantValue]) =>
              Object.entries(configuration).some(
                ([configKey, configValue]) =>
                  variantKey === configKey &&
                  variantValue.assetId === configValue.assetId,
              ),
          ) && Object.keys(variant.node.configuration).length > 0,
      );

    if (variant) {
      return variant;
    }

    return null;
  };

  const getAllVariants = () => {
    const mainProduct = products.find((product) =>
      product.tags.includes('Customizer:Main_Product'),
    );

    // If the main product has more than one variant, use the second variant
    // as the first variant is used for PDP pricing while the second one
    // is used for the base-price of the customizer
    const mainVariant =
      mainProduct.variants.edges.length > 1
        ? mainProduct.variants.edges[1]
        : mainProduct.variants.edges[0];

    // Get all variants from all products
    let variants = products.flatMap((product) => product.variants.edges);

    // Filter variants by matching configuration with the provided configuration
    variants = variants.filter((variant) => {
      const variantConfiguration = variant.node.configuration;

      // Filter out variants that have no configuration
      if (Object.keys(variantConfiguration).length === 0) {
        return false;
      }

      // Check if all configuration entries in the variant match with the provided configuration
      // Ignore Image and Art attributes
      return Object.entries(variantConfiguration).every(
        ([variantKey, variantValue]) =>
          Object.entries(configuration).some(
            ([configKey, configValue]) =>
              variantKey === configKey &&
              variantValue?.assetId === configValue?.assetId &&
              !variantKey.includes('Image') &&
              !variantKey.includes('Art'),
          ),
      );
    });

    // Add Texts
    const addTexts = (textType, tagType) => {
      const attributeName = tagType.split('Threekit:')[1].replace(/_/g, ' ');
      const textProduct = products.find(
        (product) =>
          product.tags.includes(tagType) &&
          !product.tags.includes('cstm:one_item_limit'),
      );
      if (textProduct && textType !== '') {
        variants.push({
          ...textProduct.variants.edges[0],
          threekitAttribute: attributeName,
        });
      }
    };

    if (frontTextLine1 !== '' && frontTextLine2 !== '') {
      addTexts(frontTextLine1, 'Threekit:Add_Front_Text');
    } else if (frontTextLine1 !== '') {
      addTexts(frontTextLine1, 'Threekit:Add_Front_Text');
    } else if (frontTextLine2 !== '') {
      addTexts(frontTextLine2, 'Threekit:Add_Back_Text');
    }

    if (backTextLine1 !== '' && backTextLine2 !== '') {
      addTexts(backTextLine1, 'Threekit:Add_Back_Text');
    } else if (backTextLine1 !== '') {
      addTexts(backTextLine1, 'Threekit:Add_Back_Text');
    } else if (backTextLine2 !== '') {
      addTexts(backTextLine2, 'Threekit:Add_Back_Text');
    }

    if (personalizeTextOne === '' && personalizeTextTwo !== '') {
      addTexts(personalizeTextTwo, 'Threekit:Add_Text_Line_2');
    } else if (personalizeTextOne !== '') {
      addTexts(personalizeTextOne, 'Threekit:Add_Text_Line_1');
    } else if (personalizeTextTwo !== '') {
      addTexts(personalizeTextTwo, 'Threekit:Add_Text_Line_2');
    }

    // Some products only charge for one text item,
    // so this tag handles that case
    const textProduct = products.find(
      (product) =>
        product.tags.includes('cstm:one_item_limit') &&
        (product.tags.includes('Threekit:Add_Front_Text') ||
          product.tags.includes('Threekit:Add_Front_Text')),
    );

    if (textProduct && (frontTextLine1 !== '' || backTextLine1 !== '')) {
      if (frontTextLine1 !== '' && backTextLine1 !== '') {
        variants.push({
          ...textProduct.variants.edges[0],
          threekitAttribute: 'Add Front Text',
        });
        // set the other price to 0.00
        variants.push({
          ...textProduct.variants.edges[0],
          threekitAttribute: 'Add Back Text',
          node: {
            ...textProduct.variants.edges[0].node,
            priceV2: {
              ...textProduct.variants.edges[0].node.priceV2,
              amount: '0.00',
            },
          },
        });
      } else if (frontTextLine1 !== '') {
        variants.push({
          ...textProduct.variants.edges[0],
          threekitAttribute: 'Add Front Text',
        });
      } else if (backTextLine1 !== '') {
        variants.push({
          ...textProduct.variants.edges[0],
          threekitAttribute: 'Add Back Text',
        });
      }
    }

    const addCustomImage = (image, tagType) => {
      const attributeName = tagType.split('Threekit:')[1].replace(/_/g, ' ');
      const customImageProduct = products.find(
        (product) =>
          product.tags.includes(tagType) &&
          !product.tags.includes('cstm:one_item_limit'),
      );
      if (customImageProduct && image) {
        const customImageVariant = customImageProduct.variants.edges.find(
          (variant) => variant.node.title === 'Upload',
        );
        variants.push({
          ...customImageVariant,
          threekitAttribute: attributeName,
        });
      }
    };

    // Add Custom Image Uploads
    if (frontCustomImage) {
      addCustomImage(frontCustomImage, 'Threekit:Add_Front_Image');
      addCustomImage(frontCustomImage, 'Threekit:Upload_Image');
    }

    if (backCustomImage) {
      addCustomImage(backCustomImage, 'Threekit:Add_Back_Image');
    }

    // Some products only charge for one upload item,
    // so this tag handles that case
    const uploadProduct = products.find(
      (product) =>
        product.tags.includes('cstm:one_item_limit') &&
        (product.tags.includes('Threekit:Add_Front_Image') ||
          product.tags.includes('Threekit:Add_Back_Image')),
    );

    if (uploadProduct && (frontCustomImage || backCustomImage)) {
      if (frontCustomImage && backCustomImage) {
        variants.push({
          ...uploadProduct.variants.edges[0],
          threekitAttribute: 'Add Front Image',
        });
        variants.push({
          ...uploadProduct.variants.edges[0],
          threekitAttribute: 'Add Back Image',
          node: {
            ...uploadProduct.variants.edges[0].node,
            priceV2: {
              ...uploadProduct.variants.edges[0].node.priceV2,
              amount: '0.00',
            },
          },
        });
      } else if (frontCustomImage) {
        variants.push({
          ...uploadProduct.variants.edges[0],
          threekitAttribute: 'Add Front Image',
        });
      } else if (backCustomImage) {
        variants.push({
          ...uploadProduct.variants.edges[0],
          threekitAttribute: 'Add Back Image',
        });
      }
    }

    // Adds images for Custom Art/Patterns/and Stock Images
    const addImage = (image) => {
      const imageTag = `Threekit:${image.replace(/ /g, '_')}`;
      const imageVariant = products
        .find((product) => product.tags.includes(imageTag))
        .variants.edges.find((variant) => {
          if (imageTag.includes('Choose_Custom_Art')) {
            return variant;
          }

          if (imageTag.includes('_Image') && variant.node.title === 'Stock') {
            return variant;
          }

          if (
            imageTag.includes('_Pattern') &&
            variant.node.title === 'Pattern'
          ) {
            return variant;
          }

          return null;
        });

      if (!image) return;
      variants.push({ ...imageVariant, threekitAttribute: image });
    };

    // Add Image Uploads
    if (frontImage) {
      addImage(frontImage);
    }

    if (backImage) {
      addImage(backImage);
    }

    // Add main variant to variants array
    if (mainVariant) {
      const readableConfiguration =
        variants.flatMap((variant) =>
          Object.entries(variant.node.configuration).map(([key, value]) => ({
            attributeName: key,
            value: value.name,
          })),
        ) || {};

      variants.forEach((variant) => {
        if (variant.threekitAttribute) {
          readableConfiguration.push({
            attributeName: variant.threekitAttribute,
            value: variant.threekitAttribute,
            price: variant.node.priceV2.amount,
          });
        }
      });

      mainVariant.node = {
        ...mainVariant.node,
        isMain: true,
        knifeConfiguration: readableConfiguration,
      };
      variants.push(mainVariant);
    }

    // Calculate subtotal price of all variants
    const subtotal = variants.reduce(
      (accumulator, variant) =>
        accumulator + parseFloat(variant.node.priceV2.amount),
      0,
    );

    return variants.length > 0 ? { mainProduct, variants, subtotal } : null;
  };

  const getBackgroundColorFromSelectedVariant = () => {
    if (productTag === 'cstm:personalization') {
      let backgroundColor;
      const { variants } = getAllVariants();
      const variant = variants.find(
        (variant) => variant.node.backgroundColor?.value,
      );
      if (variant && variant.node.backgroundColor?.value) {
        backgroundColor = variant.node.backgroundColor.value;
      } else {
        backgroundColor = defaultAvailableBackgroundColors[0].hex;
      }

      return {
        ...defaultAvailableBackgroundColors[0],
        hex: backgroundColor,
        isDark: tinycolor(backgroundColor).isDark(),
      };
    }

    const products = getProductsFromAttribute('Handle Material');
    const sideColorProducts = getProductsFromAttribute(
      `${bladeSide} Handle Color`,
    );
    const colorProducts =
      sideColorProducts.length > 0
        ? sideColorProducts
        : getProductsFromAttribute(`Handle Color`);

    const selectedVariant =
      getSelectedVariant(colorProducts) || getSelectedVariant(products);
    const variantBackgroundColor =
      selectedVariant?.node?.backgroundColor?.value;
    const frontHandleConfigColor =
      selectedVariant?.node?.configuration?.[`${bladeSide} Handle Color`]
        ?.name || selectedVariant?.node?.configuration?.[`Handle Color`]?.name;
    const fallbackColor = frontHandleConfigColor
      ? frontHandleConfigColor.replace(' ', '').toLowerCase()
      : null;

    return {
      ...defaultAvailableBackgroundColors[0],
      hex: variantBackgroundColor || fallbackColor,
      wasFallback: !variantBackgroundColor,
      isDark: tinycolor(variantBackgroundColor || fallbackColor).isDark(),
      default: true,
    };
  };

  const handleSetBackgroundColor = (color) => {
    const isDark = tinycolor(color.hex).isDark() || color.default;

    setBackgroundColor(color);
    setDarkMode(isDark);
  };

  const getAvailableBackgroundColors = () =>
    defaultAvailableBackgroundColors.map((color) => ({
      ...color,
      isDark: tinycolor(color.hex).isDark(),
      ...(color.useProductColor && getBackgroundColorFromSelectedVariant()),
    }));

  const parsedBackgroundColor =
    backgroundColor && backgroundColor.useProductColor
      ? getBackgroundColorFromSelectedVariant()
      : backgroundColor;

  const saveKnifeConfiguration = async (saveToDb, shortenUrl) => {
    const allVariants = getAllVariants();
    const mainVariant = allVariants.variants.find(
      (variant) => variant.node.isMain,
    );
    const contentType = 'image/jpeg';
    // Helps to retrieve the front snapshot of the blade
    let camName =
      productTag === 'cstm:personalization' || productTag === 'cstm:service'
        ? 'Snapshot camera'
        : 'Export front snapshot';
    let backCamName = 'Export back snapshot';

    // Query our camera node using embedded scene's instance
    if (
      configurator.getConfiguration()['Mobile View Orientation'] &&
      window.innerWidth < 1024 &&
      (productTag !== 'cstm:personalization' || productTag !== 'cstm:service')
    ) {
      camName = 'Export front mobile snapshot';
      backCamName = 'Export back mobile snapshot';
    }

    async function get64Image(camName) {
      const allNodes = player.scene.getAll({ evalNode: true });
      const proxyNode = allNodes[Object.keys(allNodes)[0]];
      const embedSceneInstanceId = proxyNode.Proxy.proxy.instanceId;

      const camNode = player.scene.get({
        from: embedSceneInstanceId,
        name: camName,
      });
      const cameraId = camNode?.id;

      // cstm:4000 is an awkwardly shaped product due to it being 3 knives
      const size =
        productTag === 'cstm:4000'
          ? { width: 980, height: 300 }
          : { width: 2980, height: 852 };

      const base64Image = await player.snapshotAsync({
        cameraId,
        size,
        antialiasing: 'none',
      });

      return base64Image;
    }

    const base64Image = await get64Image(camName);
    const base64BackImage = await get64Image(backCamName);

    async function getThumbnailUrlFromBase64(base64Image) {
      const binaryData = atob(base64Image.split(',')[1]);
      const uint8Array = new Uint8Array(binaryData.length);
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: contentType });
      const file = new File([blob], 'snapshot.png');
      const fd = new FormData();
      fd.append('files', file);
      fd.append('productId', player.assetId);
      fd.append('productVersion', 'v1');
      fd.append(
        'variant',
        JSON.stringify(window.configurator.getConfiguration()),
      );
      const shortId = await getShortId(env, data.authToken, fd);
      const thumbnailUrl = await getThumbnailUrl(env, shortId, data.authToken);

      const shortenedThumbnailUrl = shortenUrl
        ? await getShortUrl(thumbnailUrl)
        : thumbnailUrl;

      return {
        shortId,
        thumbnail: shortenedThumbnailUrl || thumbnailUrl,
      };
    }

    const frontThumbnail = await getThumbnailUrlFromBase64(base64Image);
    const backThumbnail =
      productTag !== 'cstm:service'
        ? await getThumbnailUrlFromBase64(base64BackImage)
        : null;

    // fetch request that gets sent to Benchmade
    if (
      productTag !== 'cstm:personalization' &&
      productTag !== 'cstm:service' &&
      saveToDb
    ) {
      const response = await postBenchmadeData(
        allVariants.subtotal,
        frontThumbnail.shortId,
        mainVariant.node.sku,
        player.assetId,
        env,
      );

      if (!response || !response.ok) {
        return null;
      }
    }

    return {
      id: frontThumbnail.shortId,
      thumbnail: frontThumbnail?.thumbnail,
      thumbnailBack: backThumbnail?.thumbnail,
    };
  };

  const getKnifeDataForCart = async (saveToDb, shortenUrl) => {
    const knifeConfiguration = await saveKnifeConfiguration(
      saveToDb,
      shortenUrl,
    );
    if (!knifeConfiguration) {
      return null;
    }

    const allVariants = getAllVariants();
    const lasermarkQuantity = parseInt(allVariants.variants.length - 1, 10);
    const isPersonalizeOrServiceProduct =
      productTag === 'cstm:personalization' || productTag === 'cstm:service';

    let configurationLineItems = [];
    let personalizationAttributes = {};

    if (isPersonalizeOrServiceProduct) {
      // Gather laser pricing details
      const textPrice = allVariants.variants.find(
        (v) => v.node.title === 'Text',
      )?.node.priceV2.amount;
      const stockImagePrice = allVariants.variants.find(
        (v) => v.node.title === 'Stock',
      )?.node.priceV2.amount;
      const customImagePrice = allVariants.variants.find(
        (v) => v.node.title === 'Upload',
      )?.node.priceV2.amount;

      // Gather image details
      let bladeImageType = 'null';
      let bladeImageUrl = 'null';
      if (stockImagePrice) {
        bladeImageType = 'stock';
        const stockImageUrl = `https://${env}.threekit.com/api/assets/thumbnail/${configuration['Stock Image'].assetId}?bearer_token=${data.authToken}`;
        bladeImageUrl = await getShortUrl(stockImageUrl);
      } else if (customImagePrice) {
        bladeImageType = 'custom';
        bladeImageUrl = await getShortUrl(customImageFileUrl);
      }

      const textLine1 = /^\s+$|^$/.exec(configuration['Add Text Line 1'])
        ? 'null'
        : configuration['Add Text Line 1'];
      const textLine2 = /^\s+$|^$/.exec(configuration['Add Text Line 2'])
        ? 'null'
        : configuration['Add Text Line 2'];
      let textFont = configuration['Choose Font']?.label;

      if ((textLine1 !== 'null' || textLine2 !== 'null') && !textFont) {
        textFont = configurator
          .getAttributes()
          .find((attr) => attr.name === 'Choose Font')?.values[0]?.name;
      }

      // Gather all threekit attributes
      personalizationAttributes = {
        // Replace empty text values with 'null'
        _text_line_1: textLine1,
        _text_line_2: textLine2,
        _text_alignment: configuration.Alignment ?? 'null',
        _text_font: textFont ?? 'null',
        _font_size: configuration['Font Size'] ?? 'null',
        _font_style: configuration['Font Format'] ?? 'null',
        _duplicate_laser_marking: personalizeDuplicateSide.toString(),
        _blade_image_url: bladeImageUrl,
        _blade_image_type: bladeImageType,
        _rendered_image_url: knifeConfiguration.thumbnail,
        _lasermark_quantity: lasermarkQuantity.toString(),
        _text_lasermark_price: textPrice ?? 'null',
        _custom_image_lasermark_price: customImagePrice ?? 'null',
        _stock_image_lasermark_price: stockImagePrice ?? 'null',
        _image_alignment: configuration['Image Alignment'] ?? 'null',
        _image_size: configuration['Image Size'] ?? 'null',
        // Future fields
        // _handle_image_type – Not needed currently
        // _handle_lasermark_price - Doesn't exist/not testable yet
      };

      configurationLineItems = Object.entries(personalizationAttributes).map(
        ([key, value]) => ({ key, value }),
      );
    }

    // TODO add all variants, line items to cart
    const items = allVariants.variants.map((variant) => {
      let knifeConfig = variant.node.knifeConfiguration;
      // If this is a 2d product we need to manually set the config displayed in cart
      if (
        isPersonalizeOrServiceProduct &&
        personalizationAttributes &&
        lasermarkQuantity > 0
      ) {
        // Configure which personalization attributes to display in cart
        const personalizationAttrMap = {
          _text_line_1: 'Text Line 1',
          _text_line_2: 'Text Line 2',
          _text_alignment: 'Text Alignment',
          _text_font: 'Font',
          _font_size: 'Text Size',
          _font_style: 'Text Style',
          _duplicate_laser_marking: 'Lasermark Both Sides',
          _blade_image_type: 'Image Type',
        };

        // Override values displayed in cart
        const perosonalizationValueMap = {
          true: 'Yes',
          false: 'No',
          stock: 'Stock',
          upload: 'Upload',
        };

        // Apply value mapping
        knifeConfig = Object.entries(personalizationAttributes)
          // Don't show any options with a 'null' configuration value
          .filter(([key, val]) => personalizationAttrMap[key] && val !== 'null')
          .map(([key, value]) => {
            const price =
              allVariants.variants.find(
                (variant) =>
                  variant.threekitAttribute?.includes(
                    personalizationAttrMap[key],
                  ) ||
                  variant.threekitAttribute?.includes(
                    perosonalizationValueMap[value],
                  ),
              )?.node?.priceV2?.amount || 0;
            return {
              attributeName: personalizationAttrMap[key],
              value: perosonalizationValueMap[value] ?? value,
              price,
            };
          });

        // Filter out Texts if there is no text config
        const hasTextConfigs = knifeConfig.some(
          (config) =>
            config.attributeName === 'Text Line 1' ||
            config.attributeName === 'Text Line 2',
        );

        if (!hasTextConfigs) {
          knifeConfig = knifeConfig.filter(
            (config) => !config.attributeName.includes('Text'),
          );
        }

        // Conditionally add non-standard details
        if (configuration['Stock Image']?.name) {
          knifeConfig.push({
            attributeName: 'Image',
            value: configuration['Stock Image'].name,
          });
        }
      }

      // Add basic line item attributes
      const node = {
        id: variant.node.id,
        quantity: 1,
        // eslint-disable-next-line quote-props
        attributes: [
          // Required for threejs
          { key: '_cart_id', value: cartId },
          { key: '_threekit_id', value: knifeConfiguration.id },
          {
            key: '_knife_config_price',
            value: allVariants.subtotal.toString(),
          },
          variant.node.configuration && {
            key: '_configuration',
            value: JSON.stringify(variant.node.configuration),
          },
          isPersonalizeOrServiceProduct &&
            variant.node.isMain && {
              key: '_personalization',
              value: 'true',
            },
          // Additional attributes for reviewing knife and cart
          knifeConfiguration.thumbnail && {
            key: '_product_image_url',
            value: knifeConfiguration.thumbnail,
          },
          knifeConfiguration.thumbnailBack && {
            key: '_product_image_url_back',
            value: knifeConfiguration.thumbnailBack,
          },
          isPersonalizeOrServiceProduct &&
            !variant.node.isMain && {
              key: '_is_component',
              value: 'true',
            },
          knifeConfig && {
            key: '_knifeConfiguration',
            value: JSON.stringify(knifeConfig),
          },
          knifeConfig && {
            key: '_product_id',
            value: variant.node.product.id,
          },
          {
            key: '_variant',
            value: JSON.stringify(variant.node),
          },
        ].filter(Boolean),
      };
      if (variant.node.isMain && variant.node.isMain !== undefined) {
        node.attributes.push(...configurationLineItems);
      }
      // Only add main/component attrs for non-2d products
      if (!isPersonalizeOrServiceProduct) {
        node.attributes.push(
          variant.node.isMain && variant.node.isMain !== undefined
            ? {
                key: '_is_main',
                value: 'true',
              }
            : {
                key: '_is_component',
                value: 'true',
              },
        );
      }
      return node;
    });

    return {
      items,
      id: knifeConfiguration.id,
    };
  };

  const getHiddenCategories = () => {
    const { variants } = getAllVariants();
    if (!variants) {
      return [];
    }

    return variants.reduce((prev, variant) => {
      if (variant.node.hideOnSelection) {
        const categories = JSON.parse(variant.node.hideOnSelection.value);
        return [...prev, ...categories];
      }
      return prev;
    }, []);
  };

  const orgId =
    env === 'preview'
      ? 'b11239ca-e41e-4e64-a20b-577354fc2ebe'
      : 'f44663ac-4d60-48df-afd8-9693edc18036';

  useEffect(() => {
    const datatableId = '157d9ed8-5274-41e4-9d95-8457cf516bf0';

    const getCacheKey = async () => {
      const cacheKeyResponse = await fetch(
        `https://${env}.threekit.com/api/datatables/${datatableId}/rows?orgId=${orgId}&bearer_token=${data.authToken}`,
        { method: 'GET', headers: { accept: 'application/json' } },
      ).then((r) => r.json());

      const cacheKey = cacheKeyResponse?.rows?.[0]?.value?.cacheKey;

      setCacheKey(cacheKey || null);
    };

    getCacheKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Context.Provider
      value={{
        cacheKey,
        orgId,
        configuration,
        setConfiguration,
        products,
        getProductsFromAttribute,
        getSelectedVariant,
        getAllVariants,
        getHiddenCategories,
        getBackgroundColorFromSelectedVariant,
        attributes,
        player,
        configurator,
        bladeSide,
        setBladeSide,
        lottie: data.lottie,
        inspo: data.inspo,
        productTag,
        authToken: data.authToken,
        reviewTerms: data.reviewTerms,
        reviewTermsSoldOnSite: data.reviewTermsSoldOnSite,
        reviewTermsNotSoldOnSite: data.reviewTermsNotSoldOnSite,
        colorAndMaterialOrder: data.colorAndMaterialOrder?.order,
        stockAndArtOrder: data.stockAndArtOrder?.order,
        blockTabs: data.blockTabs,
        locales: data.locales,
        frontTextLine1,
        setFrontTextLine1,
        frontTextLine2,
        setFrontTextLine2,
        personalizeTextOne,
        personalizeTextTwo,
        setPersonalizeTextOne,
        setPersonalizeTextTwo,
        personalizeDuplicateSide,
        setPersonalizeDuplicateSide,
        bladeColorDuplicateSide,
        setBladeColorDuplicateSide,
        backTextLine1,
        setBackTextLine1,
        backTextLine2,
        setBackTextLine2,
        frontCustomImage,
        setFrontCustomImage,
        backCustomImage,
        setBackCustomImage,
        frontImage,
        imageSeverityData,
        setFrontImage,
        backImage,
        setBackImage,
        backgroundColor: parsedBackgroundColor,
        setBackgroundColor: handleSetBackgroundColor,
        getAvailableBackgroundColors,
        darkMode,
        category,
        setCategory,
        saveKnifeConfiguration,
        getKnifeDataForCart,
        cartId,
        resetCartId,
        template: data.template,
        knifeClickActive,
        setKnifeClickActive,
        hasFlaggedContent,
        setHasFlaggedContent,
        errors,
        setIsOnBlur,
        env,
        productHandle,
        sizeErrorText: data.sizeErrorText,
        severityErrorText: data.severityErrorText,
        customImageFileUrl,
        setCustomImageFileUrl,
        moderation: data.moderation,
      }}
    >
      {children}
    </Context.Provider>
  );
};
